import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  FileText,
  HandCoins,
  KeyRound,
  Layers,
  Plus,
  Receipt,
  Undo2,
  Wallet,
} from 'lucide-react';

import { partnerAccountAPI } from '../../lib/partnerAccountApi';
import { messageErreur } from '../../lib/erreurs';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from '../ui/sonner';
import { AUTRES_OPERATIONS, FenetreGeste, FormulaireMouvement } from '../finance/NouvelleOperation';

/**
 * « Nouvelle opération » du compte: ce menu ne saisit rien lui-même, il
 * renvoie vers les écrans qui savent déjà le faire.
 *
 *  - une avance, une facture reçue, un avoir ou un remboursement ouvrent la
 *    fenêtre de l'écran Argent, préremplie avec le tiers de la société;
 *  - un acompte ou un décompte de lot mènent aux paiements des lots de la
 *    société; une avance de loyer ou un état de paiement, à ses contrats de
 *    location reçue.
 *
 * Le tiers fournisseur de la société est préparé (créé ou relié) avant d'ouvrir
 * une fenêtre: c'est lui qui porte les écritures du compte.
 */
const GESTES_FENETRE = {
  avance: { geste: 'fournisseur', icone: HandCoins, menu: 'Verser une avance' },
  'facture-recue': { geste: 'facture-recue', icone: FileText, menu: 'Facture reçue' },
  avoir: { geste: 'avoir-fournisseur', icone: Receipt, menu: 'Avoir' },
  remboursement: { geste: 'remboursement-fournisseur', icone: Undo2, menu: 'Remboursement' },
};

const GESTES_DE_LA_FENETRE_ARGENT = ['fournisseur', 'facture-recue'];

const titreAutre = (geste) => AUTRES_OPERATIONS.find((o) => o.cle === geste)?.titre || '';

export default function NouvelleOperationCompte({ societeId, onEnregistre, disabled = false }) {
  const navigate = useNavigate();
  const [fenetre, setFenetre] = useState(null); // { geste, tiers }
  const [preparation, setPreparation] = useState(false);

  const ouvrirFenetre = async (cle) => {
    const definition = GESTES_FENETRE[cle];
    setPreparation(true);
    try {
      const tiers = await partnerAccountAPI.preparerTiers(societeId);
      if (!tiers) throw new Error('tiers introuvable');
      setFenetre({ geste: definition.geste, tiers });
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de préparer le compte du partenaire'));
    } finally {
      setPreparation(false);
    }
  };

  const fermer = () => setFenetre(null);
  const termine = () => {
    setFenetre(null);
    onEnregistre?.();
  };

  const versLots = () => navigate(`/lots/paiements?transporteur=${encodeURIComponent(societeId)}`);
  const versLocations = () => navigate(`/rentals?onglet=entrantes&societe=${encodeURIComponent(societeId)}`);

  const enFenetreArgent = fenetre && GESTES_DE_LA_FENETRE_ARGENT.includes(fenetre.geste);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" disabled={disabled || preparation} data-testid="compte-nouvelle-operation">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle opération
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onSelect={() => ouvrirFenetre('avance')} className="gap-2" data-testid="raccourci-avance">
            <HandCoins className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Verser une avance
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">Lots</DropdownMenuLabel>
          <DropdownMenuItem onSelect={versLots} className="gap-2" data-testid="raccourci-acompte-lot">
            <Layers className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Acompte de lot
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={versLots} className="gap-2" data-testid="raccourci-decompte-lot">
            <FileText className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Nouveau décompte de lot
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">Location</DropdownMenuLabel>
          <DropdownMenuItem onSelect={versLocations} className="gap-2" data-testid="raccourci-avance-loyer">
            <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Avance sur fret
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={versLocations} className="gap-2" data-testid="raccourci-etat-paiement">
            <Wallet className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Nouvel état de paiement
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {['facture-recue', 'avoir', 'remboursement'].map((cle) => {
            const { icone: Icone, menu } = GESTES_FENETRE[cle];
            return (
              <DropdownMenuItem key={cle} onSelect={() => ouvrirFenetre(cle)} className="gap-2" data-testid={`raccourci-${cle}`}>
                <Icone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                {menu}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <FenetreGeste
        geste={enFenetreArgent ? fenetre.geste : undefined}
        ouvert={Boolean(enFenetreArgent)}
        prefill={{ fournisseur: fenetre?.tiers }}
        onFermer={fermer}
        onTermine={termine}
      />

      <Dialog open={Boolean(fenetre) && !enFenetreArgent} onOpenChange={(ouvert) => !ouvert && fermer()}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{titreAutre(fenetre?.geste)}</DialogTitle>
          </DialogHeader>
          {fenetre && !enFenetreArgent ? (
            <FormulaireMouvement
              key={fenetre.geste}
              geste={fenetre.geste}
              prefill={{ fournisseur: fenetre.tiers }}
              onTermine={termine}
              onAnnuler={fermer}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
